import React, { useEffect, useMemo, useState, useCallback, memo } from 'react'
import TextureTuningControls from './components/TextureTuningControls'

const isTextureUrl = (v) => typeof v === 'string' && (v.includes('/') || /\.(png|jpe?g|webp|gif|avif)$/i.test(v))

const isTextureConfig = (v) => {
    return !!v && typeof v === 'object' && !Array.isArray(v) && Object.prototype.hasOwnProperty.call(v, 'url')
}

const toNum = (v, fallback = 0) => {
    if (typeof v === 'string') v = v.replace(',', '.')
    const n = Number(v)

    return Number.isFinite(n) ? n : fallback
}

const clamp = (n, min, max) => Math.min(max, Math.max(min, n))

const normalizeTextureConfig = (value, urlFallback = null) => {
    const src = isTextureConfig(value) ? value : {}

    return {
        url: src.url ?? urlFallback ?? null,
        repeat: Array.isArray(src.repeat) && src.repeat.length >= 2
            ? [clamp(toNum(src.repeat[0], 1), 0.01, 1000), clamp(toNum(src.repeat[1], 1), 0.01, 1000)]
            : [1, 1],
        offset: Array.isArray(src.offset) && src.offset.length >= 2
            ? [clamp(toNum(src.offset[0], 0), -10, 10), clamp(toNum(src.offset[1], 0), -10, 10)]
            : [0, 0],
        rotation: clamp(toNum(src.rotation, 0), -6.28318, 6.28318),
        roughness: Number.isFinite(Number(src.roughness)) ? clamp(toNum(src.roughness, 0.72), 0, 1) : undefined,
        metalness: Number.isFinite(Number(src.metalness)) ? clamp(toNum(src.metalness, 0), 0, 1) : undefined,
        materialKey: src.materialKey || '',
        materialName: src.materialName || '',
        densityKgM3: Number(src.densityKgM3 || 0) || undefined,
        catalogRevision: Number(src.catalogRevision || 0) || 0,
    }
}

const normalizeSelectorValue = (value) => {
    if (value == null || value === '') return 'original'
    return value
}

const getDisplayValue = (value) => {
    const normalized = normalizeSelectorValue(value)

    if (isTextureConfig(normalized)) return normalized.url || ''
    if (typeof normalized === 'string') return normalized
    return 'original'
}

const getPreviewUrl = (value) => {
    const normalized = normalizeSelectorValue(value)

    if (isTextureConfig(normalized)) return normalized.url || null
    if (isTextureUrl(normalized)) return normalized
    return null
}

const getOptionLabel = (key = '') => {
    const source = String(key || '').trim()
    if (!source) return ''
    const withSpaces = source
        .replace(/[_-]+/g, ' ')
        .replace(/([a-zа-я])([A-ZА-Я])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
    return withSpaces ? withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1) : source
}

const ColorSelector = ({
    label,
    value,
    onChange,
    colors = {},
    disabled,
    textureOnly = false,
    preferredOptionOrder = [],
    originalLabel = '✦ Оригинальная текстура',
    tuningCollapsible = false,
    tuningDefaultOpen = true,
    tuningLabel = 'Тонкая настройка текстуры',
}) => {
    const options = useMemo(() => {
        const preferred = Array.isArray(preferredOptionOrder)
            ? preferredOptionOrder.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean)
            : []

        return Object.entries(colors || {})
            .filter(([, v]) => !!v)
            .filter(([, v]) => !textureOnly || isTextureConfig(v) || isTextureUrl(v))
            .sort(([aKey], [bKey]) => {
                if (!preferred.length) return getOptionLabel(aKey).localeCompare(getOptionLabel(bKey), 'ru')

                const aName = String(aKey || '').toLowerCase()
                const bName = String(bKey || '').toLowerCase()
                const aScore = preferred.findIndex((keyword) => aName.includes(keyword))
                const bScore = preferred.findIndex((keyword) => bName.includes(keyword))
                const safeAScore = aScore === -1 ? Number.MAX_SAFE_INTEGER : aScore
                const safeBScore = bScore === -1 ? Number.MAX_SAFE_INTEGER : bScore

                if (safeAScore !== safeBScore) return safeAScore - safeBScore
                return getOptionLabel(aKey).localeCompare(getOptionLabel(bKey), 'ru')
            })
    }, [colors, preferredOptionOrder, textureOnly])

    const optionConfigByValue = useMemo(() => {
        const map = new Map()
        options.forEach(([, optionValue]) => {
            if (isTextureConfig(optionValue) && optionValue.url) map.set(optionValue.url, optionValue)
        })
        return map
    }, [options])

    const effectiveValue = normalizeSelectorValue(value)
    const selectValue = getDisplayValue(effectiveValue)
    const previewUrl = getPreviewUrl(effectiveValue)
    const isOriginal = effectiveValue === 'original'
    const isSimpleColor = typeof effectiveValue === 'string' && !isOriginal && !previewUrl

    const activeTextureConfig = useMemo(() => {
        if (isTextureConfig(effectiveValue)) return normalizeTextureConfig(effectiveValue)
        if (isTextureUrl(selectValue)) return normalizeTextureConfig(null, selectValue)
        return null
    }, [effectiveValue, selectValue])

    const [densityInput, setDensityInput] = useState(() => {
        const base = activeTextureConfig?.repeat?.[0] ?? 1
        return String(base)
    })
    const [tuningOpen, setTuningOpen] = useState(() => (
        tuningCollapsible ? !!tuningDefaultOpen : true
    ))

    useEffect(() => {
        const base = activeTextureConfig?.repeat?.[0] ?? 1
        setDensityInput(String(base))
    }, [activeTextureConfig?.url, activeTextureConfig?.repeat?.[0]])

    const emitTextureConfig = useCallback((patch = {}) => {
        const next = normalizeTextureConfig({
            ...(activeTextureConfig || {}),
            ...patch,
        }, patch.url ?? activeTextureConfig?.url ?? selectValue ?? null)

        onChange?.(next)
    }, [activeTextureConfig, selectValue, onChange])

    const handleSelectChange = useCallback((event) => {
        const nextValue = event.target.value

        if (!nextValue) {
            onChange?.(null)
            return
        }

        if (nextValue === 'original') {
            onChange?.('original')
            return
        }

        if (isTextureUrl(nextValue)) {
            const prevCfg = isTextureConfig(value) ? value : null
            const optionCfg = optionConfigByValue.get(nextValue) || null
            const nextCfg = normalizeTextureConfig({
                ...(prevCfg || {}),
                ...(optionCfg || {}),
                url: nextValue,
            }, nextValue)

            setDensityInput(String(nextCfg.repeat[0] ?? 1))
            onChange?.(nextCfg)
            return
        }

        onChange?.(nextValue)
    }, [onChange, value, optionConfigByValue])

    const commitDensity = useCallback((nextRaw) => {
        const v = clamp(toNum(nextRaw, 1), 0.01, 1000)

        setDensityInput(String(v))
        emitTextureConfig({ repeat: [v, v] })
    }, [emitTextureConfig])

    const previewStyle = useMemo(() => {
        if (previewUrl) {
            return {
                backgroundImage: `url(${previewUrl})`,
            }
        }

        if (isOriginal) {
            return {}
        }

        if (isSimpleColor) {
            return {
                backgroundColor: effectiveValue,
            }
        }

        return {}
    }, [previewUrl, isOriginal, isSimpleColor, effectiveValue])

    return (
        <div className="settings-field color-selector">
            <label className="settings-field__label color-selector__label">
                {label}
            </label>

            <div className="color-selector__row">
                {(value || previewUrl || isOriginal) ? (
                    <div
                        className={[
                            'color-selector__preview',
                            isOriginal ? 'is-original' : '',
                            previewUrl ? 'is-texture' : '',
                        ].filter(Boolean).join(' ')}
                        style={previewStyle}
                    >
                        {isOriginal ? '✦' : null}
                    </div>
                ) : null}

                <select
                    className="settings-field__select color-selector__select"
                    value={selectValue ?? ''}
                    onChange={handleSelectChange}
                    disabled={disabled}
                >
                    <option value="original">{originalLabel}</option>

                    {options.map(([key, colorValue]) => (
                        <option key={key} value={isTextureConfig(colorValue) ? colorValue.url : colorValue}>
                            {getOptionLabel(key)}
                        </option>
                    ))}
                </select>
            </div>

            {activeTextureConfig?.url ? (
                tuningCollapsible ? (
                    <div className={`color-selector__tuningDisclosure ${tuningOpen ? 'is-open' : 'is-collapsed'}`}>
                        <button
                            type="button"
                            className="color-selector__tuningToggle"
                            onClick={() => setTuningOpen((previous) => !previous)}
                            aria-expanded={tuningOpen}
                            disabled={disabled}
                        >
                            <span>{tuningLabel}</span>
                            <b aria-hidden="true">⌄</b>
                        </button>
                        {tuningOpen ? (
                            <div className="color-selector__tuning">
                                <TextureTuningControls
                                    mode="density"
                                    value={densityInput}
                                    onValueChange={(next) => setDensityInput(next)}
                                    onValueCommit={commitDensity}
                                    onReset={() => commitDensity('1')}
                                    advancedValue={activeTextureConfig}
                                    onAdvancedChange={emitTextureConfig}
                                    disabled={disabled}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="color-selector__tuning">
                        <TextureTuningControls
                            mode="density"
                            value={densityInput}
                            onValueChange={(next) => setDensityInput(next)}
                            onValueCommit={commitDensity}
                            onReset={() => commitDensity('1')}
                            advancedValue={activeTextureConfig}
                            onAdvancedChange={emitTextureConfig}
                            disabled={disabled}
                        />
                    </div>
                )
            ) : null}
        </div>
    )
}

export default memo(ColorSelector)
